<template>
  <div>
    <h4 class="text-center mb-3">Multiples dépenses</h4>

    <p class="glass-label mb-2" style="font-size: 0.85rem; opacity: 0.7">
      Une dépense par ligne : <strong>nom catégorie montant</strong><br />
      Séparés par tabulation ou espaces. <br />
      Saisie à la date du jour. <br />
      Montant en euros (ex: 45,50 ou 45.50).
    </p>

    <form @submit.prevent="submitForm" data-cy="form-multiple">
      <div class="mb-3">
        <textarea
          ref="textarea"
          class="glass-textarea w-100"
          rows="6"
          v-model="textInput"
          @input="onTextInput"
          @keydown="onKeydown"
          @focus="isFocused = true"
          @blur="onTextareaBlur"
          placeholder="Courses Alimentation 45,50&#10;Essence Transport 62&#10;..."
        ></textarea>
        <div
          v-if="suggestions.length > 0"
          class="suggestions-panel"
          :class="{ 'suggestions-panel--fixed': $isPhoneFormat || isMobile }"
          :style="
            ($isPhoneFormat || isMobile) && isFocused ? { bottom: keyboardHeight + 'px' } : {}
          "
        >
          <span
            v-for="(cat, index) in suggestions"
            :key="cat.id"
            class="suggestion-chip"
            :class="{ 'suggestion-chip--only': suggestions.length === 1 }"
            @mousedown.prevent="applySuggestion(cat)"
            @touchstart.prevent="applySuggestion(cat)"
          >
            <span
              v-if="cat.icone"
              class="suggestion-icon"
              :style="{ '--cat-color': hexToRgb(cat.couleur || '#6ab4f8') }"
            >
              <font-awesome-icon :icon="'fa-solid fa-' + cat.icone" />
            </span>
            {{ cat.libelle }}
            <span v-if="index === 0" class="tab-hint">Tab ↹</span>
          </span>
        </div>
      </div>

      <!-- Panneau de prévisualisation -->
      <div v-if="parsedLines.length > 0" class="preview-panel mb-3">
        <!-- Barre de résumé -->
        <div class="preview-summary">
          <div class="summary-badges">
            <span class="summary-badge summary-badge--valid">
              <font-awesome-icon icon="fa-solid fa-check" />
              {{ validCount }}
            </span>
            <span v-if="errorCount > 0" class="summary-badge summary-badge--error">
              <font-awesome-icon icon="fa-solid fa-triangle-exclamation" />
              {{ errorCount }} erreur{{ errorCount > 1 ? 's' : '' }}
            </span>
            <span class="summary-total">sur {{ totalCount }}</span>
          </div>
          <div
            class="summary-progress"
            role="progressbar"
            aria-valuemin="0"
            :aria-valuenow="validCount"
            :aria-valuemax="totalCount"
          >
            <div
              class="summary-progress-bar"
              :style="{ width: (validCount / totalCount) * 100 + '%' }"
            ></div>
          </div>
        </div>

        <!-- Cartes par ligne -->
        <div class="preview-lines">
          <div
            v-for="line in parsedLines"
            :key="line.lineNumber"
            class="preview-line"
            :class="{
              'preview-line--valid': line.valid,
              'preview-line--error': !line.valid
            }"
            @click="!line.valid && focusLine(line.lineNumber)"
            @keydown.enter="!line.valid && focusLine(line.lineNumber)"
            :role="!line.valid ? 'button' : undefined"
            :tabindex="!line.valid ? 0 : undefined"
            :aria-label="
              !line.valid
                ? `Ligne ${line.lineNumber} en erreur : ${line.error}. Appuyer pour corriger.`
                : undefined
            "
          >
            <!-- Numéro de ligne -->
            <span class="line-number">{{ line.lineNumber }}</span>

            <!-- Ligne valide -->
            <template v-if="line.valid">
              <div class="d-flex align-items-center gap-2">
                <span
                  class="line-cat-icon"
                  :style="{ '--cat-color': hexToRgb(line.categorie.couleur || '#6ab4f8') }"
                >
                  <font-awesome-icon
                    v-if="line.categorie.icone"
                    :icon="'fa-solid fa-' + line.categorie.icone"
                  />
                  <font-awesome-icon v-else icon="fa-solid fa-tag" />
                </span>
                <span class="line-content">
                  <span class="line-name">{{ line.nom }}</span>
                  <span class="line-cat-label">{{ line.categorie.libelle }}</span>
                </span>
              </div>
              <span class="line-amount">{{ formatAmount(line.montant) }}</span>
              <span class="line-status line-status--valid" aria-hidden="true">
                <font-awesome-icon icon="fa-solid fa-check" />
              </span>
            </template>

            <!-- Ligne invalide (cliquable) -->
            <template v-else>
              <span class="line-content">
                <span class="line-raw">{{ line.rawLine.trim() || '—' }}</span>
                <span class="line-error-msg">{{ line.error }}</span>
              </span>
              <span class="line-edit-hint" aria-hidden="true">
                <font-awesome-icon icon="fa-solid fa-pen" />
              </span>
            </template>
          </div>
        </div>
      </div>

      <div class="form-actions">
        <button
          type="button"
          class="glass-btn-danger-outlined rounded-5 py-2 px-3"
          @click="$emit('close')"
        >
          Annuler
        </button>
        <button
          type="submit"
          class="glass-btn-success rounded-5 py-2 px-3"
          :disabled="isSubmitting || validCount === 0"
        >
          Ajouter ({{ validCount }})
        </button>
      </div>
    </form>
  </div>
</template>

<script>
import useCategorieStore from '@/stores/categorie'
import useCategorieDepenseStore from '@/stores/categorieDepense'
import { normalize, toCurrencyEUR } from '@/utils/normalizer'

export default {
  name: 'DepenseMultipleFormBlock',

  setup() {
    const categorieStore = useCategorieStore()
    const categorieDepenseStore = useCategorieDepenseStore()
    return { categorieStore, categorieDepenseStore }
  },

  emits: ['close'],

  data() {
    return {
      textInput: '',
      parsedLines: [],
      isSubmitting: false,
      debounceTimer: null,
      suggestions: [],
      currentToken: '',
      currentTokenStart: -1,
      currentTokenEnd: -1,
      isMobile: false,
      isFocused: false,
      keyboardHeight: 0
    }
  },

  computed: {
    validLines() {
      return this.parsedLines.filter((l) => l.valid)
    },
    errorLines() {
      return this.parsedLines.filter((l) => !l.valid)
    },
    validCount() {
      return this.validLines.length
    },
    errorCount() {
      return this.errorLines.length
    },
    totalCount() {
      return this.parsedLines.length
    }
  },

  mounted() {
    this.categorieStore.loadCategories(true)

    // Détection mobile (même pattern que App.vue)
    this._mq = window.matchMedia('(max-width: 768px)')
    this._mqHandler = (e) => {
      this.isMobile = e.matches
    }
    this._mq.addEventListener('change', this._mqHandler)
    this.isMobile = this._mq.matches

    // Détection hauteur clavier (resize + scroll pour iOS)
    this._onViewportChange = () => {
      const vv = window.visualViewport
      if (vv) this.keyboardHeight = Math.max(0, window.innerHeight - vv.height - vv.offsetTop)
    }
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', this._onViewportChange)
      window.visualViewport.addEventListener('scroll', this._onViewportChange)
    }
  },

  beforeUnmount() {
    if (this._mq && this._mqHandler) {
      this._mq.removeEventListener('change', this._mqHandler)
    }
    if (window.visualViewport && this._onViewportChange) {
      window.visualViewport.removeEventListener('resize', this._onViewportChange)
      window.visualViewport.removeEventListener('scroll', this._onViewportChange)
    }
  },

  methods: {
    onTextInput() {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = setTimeout(() => {
        this.parseAllLines()
      }, 300)
      this.updateSuggestions()
    },

    parseAllLines() {
      const lines = this.textInput.split('\n')
      this.parsedLines = []

      lines.forEach((line, index) => {
        const trimmed = line.trim()
        if (trimmed === '') return

        const result = this.parseLine(trimmed)
        result.lineNumber = index + 1
        result.rawLine = line
        this.parsedLines.push(result)
      })
    },

    parseLine(line) {
      let parts

      if (line.includes('\t')) {
        // Délimiteur tabulation
        parts = line
          .split('\t')
          .map((p) => p.trim())
          .filter((p) => p !== '')
        if (parts.length !== 3) {
          return {
            valid: false,
            error: 'Format attendu : nom [tab] catégorie [tab] montant (3 colonnes)'
          }
        }
        const [nom, categorieStr, montantStr] = parts
        return this.validateParts(nom, categorieStr, montantStr)
      }

      // Parsing intelligent par espaces
      const tokens = line.split(/\s+/)
      if (tokens.length < 3) {
        return { valid: false, error: 'Au moins 3 éléments requis : nom catégorie montant' }
      }

      // Dernier token = montant
      const montantStr = tokens[tokens.length - 1]
      const montant = this.parseAmount(montantStr)
      if (montant === null) {
        return { valid: false, error: `Montant invalide : "${montantStr}"` }
      }

      // Scanner les tokens restants de droite à gauche pour trouver la catégorie
      const remainingTokens = tokens.slice(0, -1)
      let categoryMatch = null
      let categoryTokenIndex = -1

      // Essayer des combinaisons de tokens pour matcher une catégorie (de droite à gauche)
      for (let i = remainingTokens.length - 1; i >= 1; i--) {
        // Essayer un seul token
        const singleToken = remainingTokens[i]
        categoryMatch = this.findCategory(singleToken)
        if (categoryMatch) {
          categoryTokenIndex = i
          break
        }

        // Essayer deux tokens combinés
        if (i >= 2) {
          const doubleToken = remainingTokens[i - 1] + ' ' + remainingTokens[i]
          categoryMatch = this.findCategory(doubleToken)
          if (categoryMatch) {
            categoryTokenIndex = i - 1
            break
          }
        }
      }

      if (!categoryMatch) {
        // Dernier recours: essayer l'avant-dernier token seul
        const lastRemaining = remainingTokens[remainingTokens.length - 1]
        categoryMatch = this.findCategory(lastRemaining)
        if (categoryMatch) {
          categoryTokenIndex = remainingTokens.length - 1
        }
      }

      if (!categoryMatch) {
        return { valid: false, error: 'Catégorie non reconnue' }
      }

      const nomTokens = remainingTokens.slice(0, categoryTokenIndex)
      if (nomTokens.length === 0) {
        return { valid: false, error: 'Nom de la dépense manquant' }
      }

      const nom = nomTokens.join(' ')

      // Valider le nom
      const validNameRegex = /^[\p{L}\p{M}\s-]+$/u
      if (!validNameRegex.test(nom)) {
        return {
          valid: false,
          error: `Nom invalide : "${nom}" (lettres, espaces et tirets uniquement)`
        }
      }

      return {
        valid: true,
        nom,
        categorie: categoryMatch,
        montant,
        error: null
      }
    },

    validateParts(nom, categorieStr, montantStr) {
      // Valider le nom
      const trimmedNom = nom.trim()
      if (trimmedNom.length > 100) {
        return { valid: false, error: `Nom trop long : "${trimmedNom}" (max 100 caractères)` }
      }

      const validNameRegex = /^[\p{L}\p{M}\p{N}\s-]+$/u
      if (!trimmedNom || !validNameRegex.test(trimmedNom)) {
        return { valid: false, error: `Nom invalide : "${trimmedNom}"` }
      }

      // Matcher la catégorie
      const categoryMatch = this.findCategory(categorieStr.trim())
      if (!categoryMatch) {
        return { valid: false, error: `Catégorie non reconnue : "${categorieStr}"` }
      }

      // Valider le montant
      const montant = this.parseAmount(montantStr.trim())
      if (montant === null) {
        return { valid: false, error: `Montant invalide : "${montantStr}"` }
      }

      return {
        valid: true,
        nom: trimmedNom,
        categorie: categoryMatch,
        montant,
        error: null
      }
    },

    findCategory(input) {
      const categories = this.categorieStore.categories
      if (!categories) return null

      const normalizedInput = normalize(input)
      if (!normalizedInput) return null

      // 1. Match exact normalisé
      const exact = categories.find((c) => normalize(c.libelle) === normalizedInput)
      if (exact) return exact

      // 2. Contains : l'un contient l'autre
      const contains = categories.find((c) => {
        const normLib = normalize(c.libelle)
        return normLib.includes(normalizedInput) || normalizedInput.includes(normLib)
      })
      if (contains) return contains

      // 3. StartsWith : le libellé commence par l'input
      const startsWith = categories.find((c) => normalize(c.libelle).startsWith(normalizedInput))
      if (startsWith) return startsWith

      return null
    },

    findAllCategories(input) {
      const categories = this.categorieStore.categories
      if (!categories) return []

      const normalizedInput = normalize(input)
      if (!normalizedInput || normalizedInput.length < 2) return []

      const exact = []
      const contains = []
      const startsWith = []

      for (const c of categories) {
        const normLib = normalize(c.libelle)
        if (normLib === normalizedInput) {
          exact.push(c)
        } else if (normLib.includes(normalizedInput) || normalizedInput.includes(normLib)) {
          contains.push(c)
        } else if (normLib.startsWith(normalizedInput)) {
          startsWith.push(c)
        }
      }

      return [...exact, ...contains, ...startsWith].slice(0, 5)
    },

    updateSuggestions() {
      const textarea = this.$refs.textarea
      if (!textarea) {
        this.suggestions = []
        return
      }

      const cursorPos = textarea.selectionStart
      const text = this.textInput

      // Find the current line
      const beforeCursor = text.substring(0, cursorPos)
      const lineStart = beforeCursor.lastIndexOf('\n') + 1
      const lineEnd = text.indexOf('\n', cursorPos)
      const currentLine = text.substring(lineStart, lineEnd === -1 ? text.length : lineEnd)
      const cursorInLine = cursorPos - lineStart

      // Tokenize: find the token under the cursor
      let tokenStart = cursorInLine
      while (
        tokenStart > 0 &&
        currentLine[tokenStart - 1] !== ' ' &&
        currentLine[tokenStart - 1] !== '\t'
      ) {
        tokenStart--
      }
      let tokenEnd = cursorInLine
      while (
        tokenEnd < currentLine.length &&
        currentLine[tokenEnd] !== ' ' &&
        currentLine[tokenEnd] !== '\t'
      ) {
        tokenEnd++
      }

      const token = currentLine.substring(tokenStart, tokenEnd)

      if (token.length >= 2) {
        this.currentToken = token
        this.currentTokenStart = lineStart + tokenStart
        this.currentTokenEnd = lineStart + tokenEnd
        this.suggestions = this.findAllCategories(token)
      } else {
        this.suggestions = []
        this.currentToken = ''
        this.currentTokenStart = -1
        this.currentTokenEnd = -1
      }
    },

    applySuggestion(categorie) {
      const before = this.textInput.substring(0, this.currentTokenStart)
      const after = this.textInput.substring(this.currentTokenEnd)
      const libelle = categorie.libelle

      this.textInput = before + libelle + after
      this.suggestions = []
      this.currentToken = ''
      this.currentTokenStart = -1
      this.currentTokenEnd = -1

      // Place cursor right after the inserted word
      this.$nextTick(() => {
        const newPos = before.length + libelle.length
        this.$refs.textarea.selectionStart = newPos
        this.$refs.textarea.selectionEnd = newPos
        this.$refs.textarea.focus()
      })

      // Re-trigger parsing
      clearTimeout(this.debounceTimer)
      this.parseAllLines()
    },

    onTextareaBlur() {
      setTimeout(() => {
        this.isFocused = false
        this.suggestions = []
      }, 200)
    },

    onKeydown(e) {
      if (e.key === 'Tab' && this.suggestions.length > 0) {
        e.preventDefault()
        this.applySuggestion(this.suggestions[0])
      }
    },

    // --- Nouvelles méthodes ---

    /**
     * Sélectionne la ligne fautive dans le textarea et y positionne le curseur.
     * @param {number} lineNumber - numéro de ligne 1-based
     */
    focusLine(lineNumber) {
      const textarea = this.$refs.textarea
      if (!textarea) return

      const lines = this.textInput.split('\n')

      // Calculer la position de début de la ligne cible
      let charStart = 0
      for (let i = 0; i < lineNumber - 1; i++) {
        charStart += lines[i].length + 1 // +1 pour le \n
      }
      const charEnd = charStart + (lines[lineNumber - 1] || '').length

      textarea.focus()
      textarea.setSelectionRange(charStart, charEnd)

      // Scroll la ligne dans la zone visible du textarea
      const lineHeight = lines.length > 0 ? textarea.scrollHeight / lines.length : 20
      textarea.scrollTop = Math.max(0, (lineNumber - 1) * lineHeight - textarea.clientHeight / 2)
    },

    /**
     * Formate un montant en euros (ex: 45,50 €)
     */
    formatAmount(amount) {
      return toCurrencyEUR(amount)
    },

    // --- Méthodes existantes ---

    hexToRgb(hex) {
      if (!hex) return '255, 255, 255'
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
      return result
        ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`
        : '255, 255, 255'
    },

    parseAmount(str) {
      if (!str) return null

      let cleaned = str.replace(/\s/g, '')

      const lastComma = cleaned.lastIndexOf(',')
      const lastDot = cleaned.lastIndexOf('.')

      if (lastComma > lastDot) {
        cleaned = cleaned.replace(/\./g, '').replace(',', '.')
      } else if (lastDot > lastComma) {
        cleaned = cleaned.replace(/,/g, '')
      }

      const value = parseFloat(cleaned)
      if (isNaN(value) || value <= 0) return null

      return Math.round(value * 100) / 100
    },

    async submitForm() {
      if (this.isSubmitting) return

      this.parseAllLines()

      if (this.validCount === 0) {
        this.$toast.error('Aucune ligne valide à ajouter')
        return
      }

      this.isSubmitting = true

      try {
        const depenseInfoList = this.validLines.map((line) => ({
          libelle: line.nom.trim(),
          idCategorie: line.categorie.id,
          date: this.$dayjs().unix(),
          montant: line.montant
        }))

        const results = await this.$toast.promise(
          this.categorieDepenseStore.addMultipleDepenses(depenseInfoList),
          {
            pending: `Import de ${depenseInfoList.length} dépense(s) en cours...`,
            success: {
              render: ({ data }) => {
                const successCount = data.filter((r) => r.success).length
                return `${successCount}/${data.length} dépense(s) importée(s) !`
              },
              autoClose: 2000
            },
            error: {
              render: "Erreur lors de l'import.",
              autoClose: 2000
            }
          }
        )

        // Reconstruire le textarea avec les lignes en erreur (API + parsing)
        const failedApiLines = []
        let validIndex = 0
        for (const parsed of this.parsedLines) {
          if (parsed.valid) {
            if (results[validIndex] && !results[validIndex].success) {
              parsed.error = 'Erreur serveur'
              parsed.valid = false
              failedApiLines.push(parsed)
            }
            validIndex++
          } else {
            failedApiLines.push(parsed)
          }
        }

        if (failedApiLines.length === 0) {
          this.textInput = ''
          this.parsedLines = []
          this.$emit('close')
        } else {
          this.textInput = failedApiLines.map((l) => l.rawLine).join('\n')
          this.parseAllLines()
        }
      } catch (error) {
        console.error("Erreur lors de l'import:", error)
      } finally {
        this.isSubmitting = false
      }
    }
  }
}
</script>

<style scoped>
/* ===== Textarea ===== */
.glass-textarea {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 12px;
  color: white;
  padding: 12px;
  resize: vertical;
  font-family: inherit;
  transition: border-color 0.2s ease;
  font-size: var(--font-size-sm) !important; /* 16px — empêche le zoom iOS */
}

.glass-textarea:focus {
  outline: none;
  border-color: rgba(255, 255, 255, 0.35);
}

.glass-textarea::placeholder {
  color: rgba(255, 255, 255, 0.3);
}

/* ===== Suggestions ===== */
.suggestions-panel {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 8px;
  margin-top: 6px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px;
}

.suggestions-panel--fixed {
  position: fixed;
  left: 12px;
  right: 12px;
  margin-top: 0;
  z-index: 1050;
  background: rgba(30, 30, 40, 0.92);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.15);
  box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.35);
}

.suggestion-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px; /* touch-friendly */
  min-height: 36px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.85);
  font-size: var(--font-size-sm);
  cursor: pointer;
  transition:
    background 0.15s ease,
    border-color 0.15s ease;
}

.suggestion-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 5px;
  background-color: rgba(var(--cat-color), 0.85);
  box-shadow: 0 2px 6px rgba(var(--cat-color), 0.4);
  font-size: 0.65rem;
  color: #fff;
}

.suggestion-chip:hover,
.suggestion-chip:active {
  background: rgba(255, 255, 255, 0.15);
  border-color: rgba(255, 255, 255, 0.3);
}

.suggestion-chip--only {
  background: rgba(255, 255, 255, 0.12);
  border-color: rgba(255, 255, 255, 0.25);
}

/* Badge Tab — desktop uniquement */
.tab-hint {
  display: inline-block;
  padding: 1px 5px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 3px;
  font-size: 0.65rem;
  color: rgba(255, 255, 255, 0.45);
  letter-spacing: 0.02em;
}

@media (max-width: 575.98px) {
  .tab-hint {
    display: none;
  }

  .suggestion-chip {
    min-height: 40px; /* était 36px, plus proche de la cible tactile 44px */
  }
}

/* ===== Panneau de prévisualisation ===== */
.preview-panel {
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  overflow: hidden;
}

/* Barre de résumé */
.preview-summary {
  padding: 10px 14px;
  background: rgba(255, 255, 255, 0.04);
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
}

.summary-badges {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 8px;
}

.summary-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
}

.summary-badge--valid {
  background: rgba(45, 171, 97, 0.15);
  color: var(--c-vert, #2dab61);
  border: 1px solid rgba(45, 171, 97, 0.3);
}

.summary-badge--error {
  background: rgba(220, 53, 69, 0.15);
  color: #e55c5a;
  border: 1px solid rgba(220, 53, 69, 0.3);
}

.summary-total {
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.4);
}

/* Barre de progression */
.summary-progress {
  height: 4px;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 2px;
  overflow: hidden;
}

.summary-progress-bar {
  height: 100%;
  background: var(--c-vert, #2dab61);
  border-radius: 2px;
  transition: width 0.35s ease;
  min-width: 4px;
}

/* Cartes par ligne */
.preview-lines {
  display: flex;
  flex-direction: column;
}

.preview-line {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 5px 10px 5px 0;
  min-height: 52px; /* cible tactile ≥ 44px */
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  transition: background 0.15s ease;
}

.preview-line:last-child {
  border-bottom: none;
}

.preview-line--valid {
  background: rgba(45, 171, 97, 0.05);
}

.preview-line--error {
  background: rgba(220, 53, 69, 0.07);
  cursor: pointer;
}

.preview-line--error:hover,
.preview-line--error:active {
  background: rgba(220, 53, 69, 0.14);
}

/* Focus clavier sur les lignes erreur (accessibilité) */
.preview-line--error:focus-visible {
  outline: 2px solid rgba(220, 53, 69, 0.7);
  outline-offset: -2px;
}

/* Numéro de ligne */
.line-number {
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.28);
  width: 16px;
  text-align: right;
  flex-shrink: 0;
}

/* Icône catégorie */
.line-cat-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background-color: rgba(var(--cat-color), 0.85);
  box-shadow: 0 2px 6px rgba(var(--cat-color), 0.35);
  font-size: 0.7rem;
  color: #fff;
  flex-shrink: 0;
}

/* Contenu textuel */
.line-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0; /* évite le débordement flex */
}

.line-name {
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.9);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.line-cat-label {
  font-size: 0.72rem;
  color: rgba(255, 255, 255, 0.4);
}

/* Montant (ligne valide) */
.line-amount {
  font-size: 0.88rem;
  font-weight: 600;
  color: var(--c-vert, #2dab61);
  white-space: nowrap;
  flex-shrink: 0;
  margin-left: auto;
}

/* Icône statut valide */
.line-status {
  width: 18px;
  text-align: center;
  flex-shrink: 0;
}

.line-status--valid {
  color: var(--c-vert, #2dab61);
  font-size: 0.78rem;
}

/* Ligne erreur : texte brut */
.line-raw {
  font-size: 0.82rem;
  color: rgba(255, 255, 255, 0.55);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Ligne erreur : message d'erreur */
.line-error-msg {
  font-size: 0.75rem;
  color: #e55c5a;
}

/* Ligne erreur : icône "corriger" */
.line-edit-hint {
  color: rgba(255, 255, 255, 0.3);
  font-size: 0.8rem;
  flex-shrink: 0;
}
</style>
